import styles from './root.module.scss';
import AppHeader from './components/header/AppHeader';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@casops/store/hooks';
import { VdsDrawer } from '@dmc/ui/vds';
import SideNav from './components/side-nav/SideNav';
import useSidePanel from '@casops/side-panels/useSidePanel';
import { Permission, useIsUserWithPermission, userIsLoggedIn } from '@casops/permissions';
import Footer from './components/footer/Footer';
import { SkeletonTheme } from 'react-loading-skeleton';
import { SuccessNotification, SuccessNotificationContext, useSuccessNotification } from '@dmc/ui';
import { useGetUiSettingsQuery } from '@casops/store/apis/settingsApi';
import { FORGOT_PASSWORD_PAGE, RESET_PASSWORD_PAGE, LOGIN_PAGE } from '@casops/shared/constants';
import { useTranslation } from 'react-i18next';
import SafetyAlertNotification from './components/safety-alert-notification/SafetyAlertNotification';

const THEME_EXCLUDE_ROUTES = [LOGIN_PAGE, FORGOT_PASSWORD_PAGE, RESET_PASSWORD_PAGE];
const HEADER_EXCLUDE_ROUTES = [...THEME_EXCLUDE_ROUTES];
const NAV_EXCLUDE_ROUTES = [...THEME_EXCLUDE_ROUTES, '/no-access'];
const GLOBAL_NOTIFICATION_EXCLUDE_ROUTES = [...NAV_EXCLUDE_ROUTES];
const FOOTER_INCLUDE_ROUTES = [...NAV_EXCLUDE_ROUTES];

const SkeletonBaseDark = '#0E0E0E'; // surface-high (dark mode)
const SkeletonBaseLight = '#FFFFFF'; // surface-high (light mode)

const SkeletonHighlightDark = '#162431'; // surface-lower (dark mode)
const SkeletonHighlightLight = '#F2F3F5'; // surface-lower (light mode)

export default function Root() {
  const { t } = useTranslation();
  const prefs = useAppSelector(state => state.prefs);
  const userData = useAppSelector(state => state.auth);

  // Fetch the UI settings so that they can be loaded into the settings slice.
  useGetUiSettingsQuery();

  const { message, countKey, showSuccessMessage, callback, callbackMessage, overrideTitle } =
    useSuccessNotification();

  const { isDrawerOpen, onDrawerClosed, activePanel } = useSidePanel();

  const location = useLocation();

  const vdsThemeClass =
    !THEME_EXCLUDE_ROUTES.includes(location.pathname) && prefs.darkTheme
      ? 'theme-dark'
      : 'theme-light';

  const isSafetyViewer = useIsUserWithPermission(Permission.SafetyViewer);

  return (
    <VdsDrawer
      className={`${styles.drawer} ${vdsThemeClass}`}
      // Note: The data-theme attribute can be targeted within CSS modules for nested, scoped styling using @theme-light/@theme-dark.
      data-theme={prefs.darkTheme ? 'dark' : 'light'}
      opened={isDrawerOpen}
      showCloseButton={true}
      backdrop={true}
      mode="over"
      position="right"
      padding="spacing-07"
      isAdaptable={true}
      onDrawerClosed={onDrawerClosed}
    >
      <SkeletonTheme
        baseColor={prefs.darkTheme ? SkeletonBaseDark : SkeletonBaseLight}
        highlightColor={prefs.darkTheme ? SkeletonHighlightDark : SkeletonHighlightLight}
      >
        <SuccessNotificationContext.Provider value={{ showSuccessMessage }}>
          <div slot="container" className={styles.dm_app}>
            {!NAV_EXCLUDE_ROUTES.includes(location.pathname) && userIsLoggedIn(userData) && (
              <SideNav />
            )}

            {/* Main content */}
            <div className={styles.dm_body}>
              {!HEADER_EXCLUDE_ROUTES.includes(location.pathname) && <AppHeader />}

              {!GLOBAL_NOTIFICATION_EXCLUDE_ROUTES.includes(location.pathname) &&
                isSafetyViewer && (
                  <div className={styles.dm_body__notifications}>
                    <SafetyAlertNotification />
                  </div>
                )}

              <main className={styles.dm_body__outlet}>
                <Outlet />
              </main>

              {FOOTER_INCLUDE_ROUTES.includes(location.pathname) && <Footer />}
            </div>

            <SuccessNotification
              countKey={countKey}
              message={message}
              onClickCallback={callback}
              callbackMessage={callbackMessage}
              title={overrideTitle || t('Success')}
            />
          </div>

          {/* Right side panel */}
          <aside slot="drawer" className={styles.parent_container}>
            <div className={styles.child_container}>{activePanel}</div>
          </aside>
        </SuccessNotificationContext.Provider>
      </SkeletonTheme>
    </VdsDrawer>
  );
}
